// Browser bootstrap for the web portal.
//
// Installs the same `window.api` contract the Electron preload and the Capacitor
// shim provide, backed by HTTP instead of IPC. The renderer cannot tell the
// difference, which is what lets all ~96 `api()` call sites and every page work
// unchanged.
//
// ORDER IS LOAD-BEARING: stores/sync.ts and stores/dataVersion.ts read window.api
// at module scope, so the shim must exist before the renderer bundle evaluates —
// hence the trailing dynamic import of ./render. Same constraint as
// src/mobile/main.tsx.

import '@/index.css' // eager so a <link> lands in index.html rather than being injected
import type { DataTopic, IpcChannel, IpcContract, IpcEnvelope } from '../shared/ipc-contract'
import type { SessionUser, SyncStatus } from '../shared/types'
import { CSRF_COOKIE, CSRF_HEADER, CLIENT_HEADER, readCookie } from './client'
import { openEventStream } from './event-stream'
import * as till from './cache/till'
import { setWebStage } from '@/stores/webStage'

/**
 * media:upload is the one binary channel: its `bytes` is a Uint8Array, which
 * JSON.stringify would turn into {"0":82,...}. The raw body IS the image and the
 * metadata rides in headers — no base64 inflation, no multipart.
 */
function uploadRequest(req: unknown): { headers: Record<string, string>; body: BodyInit } {
  const r = req as { kind: string; contentType: string; bytes: Uint8Array }
  return {
    headers: {
      'content-type': 'application/octet-stream',
      'x-media-kind': r.kind,
      'x-media-content-type': r.contentType
    },
    body: new Uint8Array(r.bytes)
  }
}

/**
 * A real HTTP round trip, with nothing in the way. `invoke` below is the cached
 * front door; this is what the cache itself calls, and what every channel it does
 * not handle falls through to.
 */
async function post<K extends IpcChannel>(
  channel: K,
  req: IpcContract[K]['req']
): Promise<IpcContract[K]['res']> {
  const binary = channel === 'media:upload' ? uploadRequest(req) : null
  const res = await fetch(`/api/ipc/${encodeURIComponent(channel)}`, {
    method: 'POST',
    headers: {
      ...(binary?.headers ?? { 'content-type': 'application/json' }),
      [CLIENT_HEADER]: '1',
      // Double-submit: echo the readable cookie back in a header a cross-origin
      // form could never set.
      [CSRF_HEADER]: readCookie(CSRF_COOKIE) ?? ''
    },
    // Wrapper object because many channels take `void`, and bare `undefined` is
    // not valid JSON.
    body: binary?.body ?? JSON.stringify({ req })
  })

  // The server always answers 200 with an envelope; a non-200 is a transport or
  // proxy fault, not a handler result.
  if (!res.ok) {
    const err = new Error(`Network error (${res.status})`) as Error & { code: string }
    err.code = 'NETWORK'
    throw err
  }

  const env = (await res.json()) as IpcEnvelope<IpcContract[K]['res']>
  if (!env.ok) {
    const err = new Error(env.error.message) as Error & { code: string }
    err.code = env.error.code
    // The session died underneath us (expired, signed out elsewhere, or evicted by
    // the same login on another machine). Drop back to sign-in rather than let
    // every screen render its own confusing error.
    if (env.error.code === 'SESSION_EXPIRED') setWebStage('anonymous')
    throw err
  }
  return env.data
}

/**
 * The cached front door.
 *
 * Everything the till reads on its hot path is answered locally when it can be, and
 * Charge writes to a durable queue instead of waiting on the network. Both live
 * behind this one function precisely so nothing in the renderer has to know: opening
 * the terminal used to be nine to twelve round trips and a sale used to be another
 * eight to ten, all on the cashier's clock.
 *
 * The cache never makes a call fail. Anything it cannot answer — a cold cache, a
 * channel it does not know, a cart needing a supervisor PIN, its own internal
 * error — falls through to `post`, which is exactly today's behaviour.
 */
async function invoke<K extends IpcChannel>(
  channel: K,
  req: IpcContract[K]['req']
): Promise<IpcContract[K]['res']> {
  try {
    const cached = await till.readThrough(channel, req)
    if (cached !== undefined) return cached as IpcContract[K]['res']

    if (channel === 'sales:checkout') {
      const sale = await till.chargeLocally(req as IpcContract['sales:checkout']['req'])
      if (sale) return sale as IpcContract[K]['res']
    }
  } catch (err) {
    // A cart the cache rejected — an unknown product, a missing serial, no stock —
    // is a real answer, not a cache miss. Re-throwing means the cashier is told
    // immediately instead of waiting for a round trip to say the same thing.
    if (isHandlerError(err)) throw err
    console.warn(`[web] cache failed on ${channel}, falling through to the server`, err)
  }

  const res = await post(channel, req)
  observe(channel, req, res)
  return res
}

/** An AppError the cache raised on the client's behalf, rather than a cache bug. */
function isHandlerError(err: unknown): boolean {
  const code = (err as { code?: unknown })?.code
  return typeof code === 'string' && code !== 'NETWORK'
}

/**
 * Watch what goes past. The cache learns who is signed in from the responses that
 * already say so, rather than making a request of its own to find out.
 */
function observe<K extends IpcChannel>(channel: K, req: unknown, res: unknown): void {
  if (channel === 'auth:session' || channel === 'auth:login') {
    void till.observeSession(res as SessionUser | null)
    return
  }
  if (channel === 'auth:setActingLocation') {
    void till.observeActingLocation(res as SessionUser)
    return
  }
  if (channel === 'auth:logout') {
    void till.forgetSession()
    return
  }
  till.observeResponse(channel, res)
  till.observeMutation(channel)
  void req
}

function bootstrap(): (topics: DataTopic[]) => void {
  // Read by src/renderer/src/lib/platform.ts. Set before the renderer evaluates.
  window.__TENDALLY_PLATFORM__ = 'web'

  const syncListeners = new Set<(status: unknown) => void>()
  const dataListeners = new Set<(topics: DataTopic[]) => void>()

  window.api = {
    invoke,
    // This used to fabricate a permanently-idle status, because reads went straight
    // to Postgres and nothing was ever pending. Sales now queue locally, so the
    // status is real — and the sidebar badge that renders it needs no change.
    onSyncStatus(cb: (status: SyncStatus) => void): () => void {
      syncListeners.add(cb as (status: unknown) => void)
      cb(till.status())
      const off = till.subscribeStatus(() => cb(till.status()))
      return () => {
        off()
        syncListeners.delete(cb as (status: unknown) => void)
      }
    },
    onDataChanged(cb: (topics: DataTopic[]) => void): () => void {
      dataListeners.add(cb)
      return () => {
        dataListeners.delete(cb)
      }
    }
    // No printHtml: lib/print.ts falls back to window.print(), which is what a
    // browser should do anyway.
  } as Window['api']

  // Each listener isolated: a throwing subscriber must not stop the others, the
  // same guarantee src/mobile/main.tsx gives.
  return (topics: DataTopic[]) => {
    for (const cb of dataListeners) {
      try {
        cb(topics)
      } catch (err) {
        console.warn('[web] onDataChanged listener threw', err)
      }
    }
  }
}

async function start(): Promise<void> {
  const fanout = bootstrap()
  till.install(post, fanout)
  // Refresh the cache BEFORE telling the renderer, so the refetch every mounted
  // screen is about to do reads fresh data instead of racing the revalidate.
  openEventStream((topics) => {
    void till.onServerTopics(topics).finally(() => fanout(topics))
  })
  await import('./render')
}

void start().catch((err) => {
  console.error('[web] bootstrap failed', err)
  const root = document.getElementById('root')
  if (root) {
    root.textContent = `Tendally failed to start: ${
      err instanceof Error ? err.message : String(err)
    }`
  }
})
